import { Root as AccordionRoot } from "@radix-ui/react-accordion";
import React, { useEffect, useRef } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";

import { AgentSchemaEditor } from "src/agents/AgentVersionPage/AgentSchemaEditor";
import { AgentToolsEditor } from "src/agents/AgentVersionPage/AgentToolsEditor";
import {
  AgentVersionConfigPanelProvider,
  ConfigSection,
  useAgentVersionConfigPanel,
} from "src/agents/AgentVersionPage/AgentVersionConfigPanelContext";
import { ExecutionSettingsEditor } from "src/agents/AgentVersionPage/ExecutionSettingsEditor";
import { JsonSchemaEditorConfig } from "src/agents/AgentVersionPage/JsonSchemaDrawer";
import { usePlaygroundContext } from "src/agents/AgentVersionPage/PlaygroundContext";
import { useSequentialAutoSave } from "src/agents/AgentVersionPage/useSequentialAutoSave";
import { AgentVersion, AgentVersionConfigurationInput } from "src/agents/types";
import OpenAIIcon from "src/assets/OpenAIIcon.svg";
import {
  accordionRootClassName,
  EditorAccordionItem as AccordionItem,
} from "src/base-components/EditorAccordionItem";
import { SkeletonPlaceholder } from "src/base-components/SkeletonPlaceholder";
import { Textarea } from "src/base-components/Textarea";
import { WhitePane } from "src/jobs/common/WhitePane";
import { useScrollIntoView } from "src/utils/useScrollIntoView";

type AgentVersionConfigurationFormProps = {
  version: AgentVersion;
  onOpenJsonEditor: (config: JsonSchemaEditorConfig) => void;
};

// IMPORTANT! Changes to the layout of the form must be reflected in the skeleton placeholder below.
export const AgentVersionConfigPanel: React.FC<
  AgentVersionConfigurationFormProps
> = ({ version, onOpenJsonEditor }) => {
  const outputSchema = version.configuration.output_schema;
  const initialOutputMode =
    outputSchema && Object.keys(outputSchema.properties || {}).length > 0
      ? "structured"
      : "text";

  return (
    <AgentVersionConfigPanelProvider initialOutputMode={initialOutputMode}>
      <AgentVersionConfigPanelContent
        version={version}
        onOpenJsonEditor={onOpenJsonEditor}
      />
    </AgentVersionConfigPanelProvider>
  );
};

/**
 * Main form that contains all the agent config options
 */
const AgentVersionConfigPanelContent: React.FC<
  AgentVersionConfigurationFormProps
> = ({ version, onOpenJsonEditor }) => {
  const formMethods = useForm<AgentVersionConfigurationInput>({
    defaultValues: version.configuration,
  });

  const {
    accordionValues,
    setAccordionValues,
    registerScrollFn,
    scrollToSection,
  } = useAgentVersionConfigPanel();

  // Scroll hooks for each accordion section
  // Note: Schema scroll is registered by AgentSchemaEditor to scroll to a lower position
  const { ref: modelPromptRef, scrollIntoView: scrollModelPrompt } =
    useScrollIntoView();
  const { ref: baseSettingsRef, scrollIntoView: scrollBaseSettings } =
    useScrollIntoView();
  const { ref: toolsRef, scrollIntoView: scrollTools } = useScrollIntoView();

  // Register scroll functions with context
  useEffect(() => {
    registerScrollFn(ConfigSection.ModelPrompt, scrollModelPrompt);
    registerScrollFn(ConfigSection.BaseSettings, scrollBaseSettings);
    registerScrollFn(ConfigSection.Tools, scrollTools);
    // Schema section scroll function is registered by AgentSchemaEditor
  }, [registerScrollFn, scrollModelPrompt, scrollBaseSettings, scrollTools]);

  // Track previous accordion values to detect newly expanded sections
  const prevAccordionValuesRef = useRef<string[]>([ConfigSection.ModelPrompt]);

  const isPublished = version.status === "published";
  const { ping: prewarmPing } = usePlaygroundContext();

  const { save } = useSequentialAutoSave({
    agentId: version.agent_id,
    versionId: version.id,
    name: version.name,
    description: version.description,
    initialEtag: version.etag,
    disabled: isPublished,
    onSave: prewarmPing,
  });

  // Subscribe to form changes and save changes to the server
  useEffect(() => {
    if (isPublished) return;

    const unsubscribe = formMethods.subscribe({
      formState: { values: true },
      callback: ({ values: configuration }) => save(configuration),
    });

    return () => {
      unsubscribe();
      save.flush(); // Flush debounce on unmount
    };
  }, [formMethods, isPublished, save]);

  return (
    <FormProvider {...formMethods}>
      <div className="h-auto">
        <WhitePane noMinWidth>
          <div className="h-full w-full px-5 py-4">
            <div className="mb-6 border-b border-gray-100 pb-4">
              <h2 className="text-gray-800 font-inter-semibold-13px">
                Configure Agent
              </h2>
              <p className="mt-1 text-gray-500 font-inter-normal-12px">
                Define how your agent behaves and executes actions.
              </p>
            </div>
            <AccordionRoot
              className={accordionRootClassName}
              type="multiple"
              value={accordionValues}
              onValueChange={(values) => {
                // Find newly expanded section (value that wasn't in previous state)
                const newlyExpanded = values.find(
                  (v) => !prevAccordionValuesRef.current.includes(v),
                );
                prevAccordionValuesRef.current = values;
                setAccordionValues(values);

                // Only scroll if a section was expanded, not collapsed
                // Delay to wait for accordion animation to complete
                if (newlyExpanded) {
                  setTimeout(
                    () => scrollToSection(newlyExpanded as ConfigSection),
                    100,
                  );
                }
              }}
            >
              <div ref={modelPromptRef}>
                <AccordionItem
                  className="pb-7 pl-[26px] pt-2"
                  dataLoc="model-prompt-accordion"
                  title="Model & Prompt"
                  value={ConfigSection.ModelPrompt}
                >
                  <div className="rounded-lg bg-gray-50 px-4 py-3">
                    <div className="mb-6">
                      <label className="mb-2 inline-block text-gray-800 font-inter-semibold-13px">
                        Model
                      </label>
                      <div className="flex w-80 cursor-default items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2">
                        <img
                          alt="OpenAI"
                          className="h-5 w-5"
                          src={OpenAIIcon}
                        />
                        <span className="text-gray-800 font-inter-normal-12px">
                          GPT-5
                        </span>
                      </div>
                    </div>
                    <div>
                      <label className="mb-2 inline-block text-gray-800 font-inter-semibold-13px">
                        Prompt
                      </label>
                      <div className="h-90">
                        <Controller
                          control={formMethods.control}
                          name="model_config.system_prompt"
                          render={({ field }) => (
                            <Textarea
                              disabled={isPublished}
                              value={field.value || ""}
                              fullHeight
                              fullWidth
                              onChange={field.onChange}
                            />
                          )}
                        />
                      </div>
                    </div>
                  </div>
                </AccordionItem>
              </div>
              <div ref={baseSettingsRef}>
                <AccordionItem
                  className="pb-7 pl-[26px] pt-2"
                  dataLoc="iterations-temperature-accordion"
                  title="Max iterations & Temperature"
                  value={ConfigSection.BaseSettings}
                >
                  <ExecutionSettingsEditor immutable={isPublished} />
                </AccordionItem>
              </div>
              <div ref={toolsRef}>
                <AccordionItem
                  className="pb-6 pl-[26px] pr-5"
                  dataLoc="tools-accordion"
                  title="Tools"
                  value={ConfigSection.Tools}
                >
                  <AgentToolsEditor immutable={isPublished} />
                </AccordionItem>
              </div>
              <div>
                {/* forceMount keeps tab state (Input/Output) preserved when accordion is collapsed */}
                <AccordionItem
                  className="pb-6 pl-[26px] pr-5"
                  dataLoc="schema-accordion"
                  title="Schema"
                  value={ConfigSection.Schema}
                  forceMount
                >
                  <AgentSchemaEditor
                    immutable={isPublished}
                    onOpenJsonEditor={onOpenJsonEditor}
                  />
                </AccordionItem>
              </div>
            </AccordionRoot>
          </div>
        </WhitePane>
      </div>
    </FormProvider>
  );
};

export const AgentVersionConfigPanelSkeleton: React.FC = () => {
  return (
    <WhitePane noMinWidth>
      <div className="h-full w-full px-5 py-4">
        <div className="mb-6 border-b border-gray-100 pb-4">
          <h2 className="text-gray-800 font-inter-semibold-13px">
            Configure Agent
          </h2>
          <p className="mt-1 text-gray-500 font-inter-normal-12px">
            Define how your agent behaves and executes actions.
          </p>
        </div>
        <div className={accordionRootClassName}>
          <div className="border-b border-gray-200">
            <div className="flex h-13 items-center px-4">
              <SkeletonPlaceholder height="h-5" width="w-32" />
            </div>
            <div className="pb-7 pl-7 pr-5 pt-2">
              <div className="rounded-lg bg-gray-50 px-4 py-3">
                <div className="mb-6">
                  <SkeletonPlaceholder height="h-4" width="w-16" />
                  <div className="mt-2">
                    <SkeletonPlaceholder height="h-10" width="w-80" />
                  </div>
                </div>
                <div>
                  <SkeletonPlaceholder height="h-4" width="w-16" />
                  <div className="mt-2 h-90">
                    <SkeletonPlaceholder height="h-full" width="w-full" />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="flex h-13 items-center border-b border-gray-200 px-4">
            <SkeletonPlaceholder height="h-5" width="w-48" />
          </div>
          <div className="flex h-13 items-center border-b border-gray-200 px-4">
            <SkeletonPlaceholder height="h-5" width="w-24" />
          </div>
          <div className="flex h-13 items-center px-4">
            <SkeletonPlaceholder height="h-5" width="w-28" />
          </div>
        </div>
      </div>
    </WhitePane>
  );
};
